import React from 'react';
import { Home, Car, Building2, Briefcase, TrendingUp, RefreshCw, PiggyBank, Package } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Home,
      title: 'First Home Loans',
      description: 'Turn your dream of homeownership into reality with tailored first home buyer solutions.',
      features: ['Government grants assistance', 'Low deposit options', 'First-time buyer guidance'],
      color: 'from-blue-500 to-blue-600',
      href: '/first-home-buyers',
    },
    {
      icon: TrendingUp,
      title: 'Next Home',
      description: 'Upsizing, downsizing, or seeking a new scene? We simplify your next move.',
      features: ['Equity assessment', 'Upgrade solutions', 'Seamless transitions'],
      color: 'from-purple-500 to-purple-600',
      href: '/next-home',
    },
    {
      icon: RefreshCw,
      title: 'Refinance',
      description: 'Better rates and terms aligned with your financial goals.',
      features: ['Rate comparison', 'Cash out options', 'Debt consolidation'],
      color: 'from-green-500 to-green-600',
      href: '/refinance',
    },
    {
      icon: Building2,
      title: 'Investment Property',
      description: 'Maximize returns on your property investment portfolio.',
      features: ['Tax-effective structures', 'Portfolio growth strategies', 'Investment analysis'],
      color: 'from-yellow-500 to-yellow-600',
      href: '/investment-property',
    },
    {
      icon: PiggyBank,
      title: 'Personal Loans',
      description: 'Consolidate debt, plan vacations, or tackle unexpected expenses.',
      features: ['Competitive rates', 'Flexible terms', 'Quick approval'],
      color: 'from-pink-500 to-pink-600',
      href: '/personal-loans',
    },
    {
      icon: Briefcase,
      title: 'Commercial Finance',
      description: 'Flexible commercial property loans tailored to your business needs.',
      features: ['Business loans', 'Commercial property', 'Equipment finance'],
      color: 'from-indigo-500 to-indigo-600',
      href: '/commercial-loans',
    },
    {
      icon: Package,
      title: 'Asset Finance',
      description: 'Upgrade machinery, purchase software, or expand operations.',
      features: ['Equipment leasing', 'Vehicle finance', 'Technology upgrades'],
      color: 'from-teal-500 to-teal-600',
      href: '/asset-finance',
    },
    {
      icon: Car,
      title: 'Car Loans',
      description: 'Gear up for your next adventure with smooth car loan solutions.',
      features: ['New & used vehicles', 'Competitive rates', 'Fast approval'],
      color: 'from-red-500 to-red-600',
      href: '/car-loans',
    },
  ];

  return (
    <section id="services" className="py-20 bg-white scroll-mt-20" data-testid="services-section">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-flex items-center justify-center space-x-2 bg-[#99DF63]/10 px-6 py-2 rounded-full mb-6">
            <Briefcase className="w-5 h-5 text-[#00594C]" />
            <span className="text-[#00594C] font-semibold">Our Services</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#00594C] mb-4" data-testid="services-title">
            Comprehensive Loan Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From first homes to business expansion, we offer expert guidance across all loan types
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 card-hover group"
                data-testid={`service-card-${index}`}
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#00594C] mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 text-sm">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start space-x-2 text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#99DF63] mt-1.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={service.href}
                  className="mt-6 text-[#00594C] font-semibold hover:text-[#99DF63] transition-colors flex items-center space-x-1 group"
                >
                  <span>Learn More</span>
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;